import React, { useState, useEffect, useMemo, useCallback } from "react";
import CalendarHeader from "../components/CalendarHeader";
import CalendarGrid from "../components/CalendarGrid";
import {
  getBookings,
  BookingsResponse,
  getBookingEdit,
} from "../services/bookings";
import { getBlockTimes } from "../services/blockTimes";
import { Booking } from "../types/booking";
import GlobalLoader from "../components/ui/GlobalLoader";
import { useEmployees } from "../hooks/useEmployees";
import { useToast } from "../hooks/useToast";

import { startOfDayISO } from "../lib/dateUtil";

const BookingsPage: React.FC = () => {
  const toast = useToast();
  const [realBookings, setRealBookings] = useState<Booking[]>([]);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(false);
  const [mockBookings, setMockBookings] = useState<Booking[]>([]);

  const [startDate, setStartDate] = useState<string>(
    startOfDayISO(new Date())
  );
  const [endDate, setEndDate] = useState<string>(
    startOfDayISO(new Date())
  );
  const [status, setStatus] = useState<string>("all");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const { staffList, loading: employeesLoading } = useEmployees();
  const [staffLoaded, setStaffLoaded] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<string | null>("");
  const [calendarView, setCalendarView] = useState<"day" | "week">("day");
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refreshBookings = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (staffList.length > 0) {
      setStaffLoaded(true);
    }
  }, [staffList]);
  const staticStaff = useMemo(
    () =>
      [
        "Alice Williams",
        "Bob Smith",
        "Charlie Brown",
        "David Davis",
        "Eve Miller",
        "Frank Wilson",
        "Grace Moore",
        "Hank Taylor",
        "Ivy Anderson",
        "Jack Thomas",
      ].map((name, i) => ({
        id: 1000 + i,
        name,
        color: [
          "#885ABB",
          "#5AB7BB",
          "#C96EBE",
          "#837EED",
          "#FFB74D",
          "#4DB6AC",
          "#E57373",
        ][i % 7],
      })),
    []
  );

  const mergedStaffList = useMemo(() => [...staticStaff], [staticStaff]);

  useEffect(() => {
    const fetchRealBookings = async () => {
      if (!startDate || !endDate) return;

      setLoading(true);
      setIsLoadingStatus(true);
      setError("");

      try {
        const response: BookingsResponse = await getBookings({
          filter_status: "all",
          startDate,
          endDate,
        });

        const bookingIds = response.data.map((b: any) => b.id);
        const bookingEdits = await Promise.all(
          bookingIds.map((id: number) =>
            getBookingEdit(id).catch((err) => {
              console.warn(`getBookingEdit failed for ${id}:`, err);
              return null;
            })
          )
        );

        const uiBookings: Booking[] = response.data.flatMap(
          (b: any, index: number) => {
            const edit = bookingEdits[index]?.booking;
            // TODO: FIXME: The /admin/bookings API currently returns incorrect booking times.
            // We are temporarily using the time from the /admin/bookings/{id}/edit API because it returns the correct time.
            // This is a workaround and should be reverted/cleaned up once the main bookings API is fixed.
            const startRaw = edit?.date_time || edit?.start || b.date_time;
            let start: Date;
            if (startRaw) {
              // Try native parsing first (after stripping 'Z' and microseconds as per workaround)
              // The API incorrectly appends 'Z' to local times, so we remove it to parse as local.
              let cleanedTime = startRaw.replace(/\.?\d{0,6}Z?$/, "");
              let nativeDate = new Date(cleanedTime);

              if (!isNaN(nativeDate.getTime())) {
                start = nativeDate;
              } else {
                // Fallback to manual dash-based parsing for ISO-like strings if native fails
                const [dPart, tPart] = cleanedTime.split(/[ T]/);
                if (dPart && dPart.includes("-")) {
                  const [y, month, d] = dPart.split("-").map(Number);
                  const timeParts = (tPart || "00:00")
                    .split(":")
                    .map((part: string) => part.replace(/[^0-9]/g, ""));
                  const h = Number(timeParts[0] || 0);
                  const m = Number(timeParts[1] || 0);

                  let finalH = h;
                  if (tPart && tPart.toLowerCase().includes("pm") && h < 12)
                    finalH += 12;
                  if (tPart && tPart.toLowerCase().includes("am") && h === 12)
                    finalH = 0;

                  start = new Date(y, month - 1, d, finalH, m);
                } else {
                  start = new Date();
                }
              }

              // Final validation check to prevent crashes
              if (isNaN(start.getTime())) {
                console.warn(`Failed to parse date for booking ${b.id}:`, startRaw);
                start = new Date();
              }
            } else {
              start = new Date();
            }

            // Normalize status to match BookingCard expectations
            let normalizedStatus: "booked" | "completed" | "pending" | "in_progress" | "no_show" | "cancelled" = "booked";
            const rawStatus = (b.status || "").toLowerCase();

            if (rawStatus === "completed" || rawStatus === "complete") {
              normalizedStatus = "completed";
            } else if (rawStatus === "pending") {
              normalizedStatus = "pending";
            } else if (rawStatus === "in progress" || rawStatus === "in_progress") {
              normalizedStatus = "in_progress";
            } else if (rawStatus === "no show" || rawStatus === "no_show") {
              normalizedStatus = "no_show";
            } else if (rawStatus === "cancelled" || rawStatus === "canceled") {
              normalizedStatus = "cancelled";
            } else {
              normalizedStatus = "booked";
            }

            // Build one calendar entry per assigned employee so the booking
            // appears in each stylist's column when multiple stylists are involved.
            const employees: any[] = Array.isArray(b.employees) && b.employees.length > 0
              ? b.employees
              : [null]; // single null entry = fall through to fallback name

            return employees.map((emp: any, empIdx: number) => {
              // Resolve which booking items belong to this employee.
              // Backend stores employee_id on each booking_item when per-service stylists are used.
              let empItems: any[] = [];
              if (edit?.items && Array.isArray(edit.items)) {
                if (employees.length > 1) {
                  const empId = emp?.id;
                  const byEmpId = empId
                    ? edit.items.filter((item: any) => item.employee_id === empId)
                    : [];
                  // Fall back to positional match if employee_id not on items
                  empItems = byEmpId.length > 0 ? byEmpId : [edit.items[empIdx]].filter(Boolean);
                } else {
                  empItems = edit.items;
                }
                // If still empty, use all items
                if (empItems.length === 0) empItems = edit.items;
              }

              // Duration: sum this employee's items
              let totalMinutes = 0;
              empItems.forEach((item: any) => {
                const serviceTime = item?.business_service?.time || 0;
                const timeType = item?.business_service?.time_type || 'minutes';
                totalMinutes += timeType === 'hours' ? serviceTime * 60 : serviceTime;
              });

              // Fallback to business_services if items not available
              const editServices = bookingEdits[index]?.business_services;
              if (totalMinutes === 0 && editServices?.[0]?.time) {
                const serviceTime = editServices[0].time;
                const timeType = editServices[0].time_type || 'minutes';
                totalMinutes = timeType === 'hours' ? serviceTime * 60 : serviceTime;
              }

              // Final fallback to 60 minutes if still no duration found
              if (totalMinutes === 0) {
                totalMinutes = 60;
              }

              const end = new Date(start.getTime() + totalMinutes * 60000);

              const staffName = emp?.name || edit?.staffName || b.employees?.[0]?.name || "";

              const categoryColor = empItems[0]?.business_service?.category?.color_code
                || edit?.items?.[0]?.business_service?.category?.color_code
                || null;

              const serviceNamesFrom = (items: any[] | undefined | null) =>
                (items || [])
                  .map(
                    (item: any) =>
                      item?.business_service?.name ||
                      item?.businessService?.name ||
                      item?.product?.name ||
                      null
                  )
                  .filter(Boolean)
                  .join(", ");

              const serviceName =
                serviceNamesFrom(empItems) ||
                editServices?.[0]?.name ||
                serviceNamesFrom(edit?.items) ||
                "";

              const phone =
                edit?.user?.formatted_mobile ||
                edit?.user?.mobile_with_code ||
                edit?.user?.mobile ||
                edit?.user?.phone ||
                b.mobile ||
                b.phone ||
                "";

              // Amount: sum this employee's items' amounts for accurate per-stylist price display.
              // Falls back to full booking amount when items don't have per-employee breakdown.
              let empAmountToPay: number = b.amount_to_pay;
              let empAmountPaid: number = b.amount_paid;
              let empPendingAmount: number = b.pending_amount ?? 0;

              if (employees.length > 1 && empItems.length > 0) {
                const empTotal = empItems.reduce((sum: number, item: any) => {
                  return sum + (Number(item.amount) || 0);
                }, 0);

                if (empTotal > 0) {
                  // Scale paid/pending proportionally to this employee's share
                  const totalAmount = Number(b.amount_to_pay) || 1;
                  const ratio = empTotal / totalAmount;
                  empAmountToPay = empTotal;
                  empAmountPaid = Math.min(empTotal, Math.round(Number(b.amount_paid) * ratio * 100) / 100);
                  empPendingAmount = Math.max(0, empAmountToPay - empAmountPaid);
                }
              }

              return {
                // Compound id for React key uniqueness; bookingId holds the real id for API calls
                id: employees.length > 1 ? (b.id * 1000 + empIdx) : b.id,
                bookingId: b.id,
                employeeId: emp?.id ? Number(emp.id) : undefined,
                client: b.name,
                phone: phone || undefined,
                service: serviceName || undefined,
                start: !isNaN(start.getTime()) ? start.toISOString() : new Date().toISOString(),
                end: !isNaN(end.getTime()) ? end.toISOString() : new Date().toISOString(),
                staff: (staffName || "").trim(),
                status: normalizedStatus,
                amountPaid: empAmountPaid,
                amountToPay: empAmountToPay,
                pendingAmount: empPendingAmount,
                currency: b.currency,
                categoryColor: categoryColor,
              } as Booking;
            });
          }
        );

        setRealBookings(uiBookings);

        let mergedForStatus = uiBookings;

        // Fetch calendar blocked times for the same range and merge as grey tiles
        try {
          const blocks = await getBlockTimes({
            start_date: startDate,
            end_date: endDate,
          });
          const blockTiles: Booking[] = blocks.map((block) => {
            const startRaw = block.start;
            const endRaw = block.end;
            const start = new Date(startRaw.replace(" ", "T"));
            const end = new Date(endRaw.replace(" ", "T"));
            return {
              id: -1 * block.id,
              blockTimeId: block.id,
              isBlocked: true,
              employeeId: block.employee_id,
              client: block.title || "Blocked",
              start: !isNaN(start.getTime())
                ? start.toISOString()
                : new Date().toISOString(),
              end: !isNaN(end.getTime())
                ? end.toISOString()
                : new Date().toISOString(),
              staff: block.employee_name || "",
              status: "blocked",
              amountPaid: 0,
              amountToPay: 0,
              pendingAmount: 0,
              categoryColor: "#6B7280",
              reason: block.reason || undefined,
            } as Booking;
          });
          mergedForStatus = [...uiBookings, ...blockTiles];
          setRealBookings(mergedForStatus);
        } catch (blockErr) {
          console.error("Failed to load blocked times:", blockErr);
        }

        const statuses = Array.from(new Set(mergedForStatus.map((b) => b.status)));
        setAvailableStatuses(statuses);

        // If current status is not in available statuses, reset to 'all'
        // But only if we have at least one booking
        if (mergedForStatus.length > 0 && status !== "all" && !statuses.includes(status as any)) {
          setStatus("all");
        }

      } catch (err) {
        console.error("API error fetching real bookings:", err);
        toast.error("Failed to load calendar bookings");
      } finally {
        setLoading(false);
        setIsLoadingStatus(false);
        setHasLoadedOnce(true);
      }
    };

    fetchRealBookings();
  }, [startDate, endDate, refreshTrigger]);

  // STATIC MOCK DATA GENERATION
  useEffect(() => {
    const generateStaticData = () => {
      const generated: Booking[] = [];
      const durations = [30, 45, 60, 90, 120];

      staticStaff.forEach((staff) => {
        const baseDate = new Date(calendarDate);
        baseDate.setHours(9, 0, 0, 0);
        let currentTime = new Date(baseDate);

        const endTimeLimit = new Date(baseDate);
        endTimeLimit.setHours(21, 0, 0, 0);

        let idx = 0;
        while (currentTime < endTimeLimit) {
          const shouldSkip = Math.random() < 0.6;
          const duration =
            durations[Math.floor(Math.random() * durations.length)];
          const start = new Date(currentTime);
          const end = new Date(start.getTime() + duration * 60000);

          if (end > endTimeLimit) break;

          if (!shouldSkip) {
            const statusOptions: ("booked" | "completed" | "pending")[] = [
              "booked",
              "completed",
              "pending",
            ];
            const randomStatus =
              statusOptions[Math.floor(Math.random() * statusOptions.length)];

            generated.push({
              id: 999000 + staff.id * 100 + idx,
              client: `${staff.name.split(" ")[0]}'s Client ${idx + 1}`,
              start: start.toISOString(),
              end: end.toISOString(),
              staff: staff.name,
              status: randomStatus,
              amountPaid: 20 * (idx + 1),
              amountToPay: 20 * (idx + 1),
              currency: "$",
              categoryColor: ["#885ABB", "#5AB7BB", "#C96EBE", "#837EED", "#FFB74D", "#4DB6AC", "#E57373"][Math.floor(Math.random() * 7)],
            });

            const gap = (1 + Math.floor(Math.random() * 3)) * 15;
            currentTime = new Date(end.getTime() + gap * 60000);
          } else {
            const jump = (3 + Math.floor(Math.random() * 6)) * 15;
            currentTime = new Date(currentTime.getTime() + jump * 60000);
          }
          idx++;
        }
      });
      setMockBookings(generated);
    };

    generateStaticData();
  }, [calendarDate]);

  // COMBINE BOTH FOR THE GRID
  const allBookings = useMemo(
    () => [...realBookings, ...mockBookings],
    [realBookings, mockBookings]
  );

  const displayBookings = useMemo(() => {
    if (status === "all") return realBookings;
    return realBookings.filter(b => b.status === status);
  }, [realBookings, status]);

  return (
    <div>
      <div className="min-h-screen bg-gray-100">
        <CalendarHeader
          startDate={startDate}
          endDate={endDate}
          status={status}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onStatusChange={setStatus}
          staffList={staffList}
          onStaffChange={setSelectedStaff}
          view={calendarView}
          setView={setCalendarView}
          selectedDate={calendarDate}
          setSelectedDate={setCalendarDate}
          availableStatuses={availableStatuses}
          isLoadingStatus={isLoadingStatus}
        />

        <div className="relative">
          {error && <p className="text-red-500 p-4">{error}</p>}

          {!hasLoadedOnce || employeesLoading ? (
            <GlobalLoader
              initialPageLoad={true}
              description="Loading calendar..."
              compensateSidebar={true}
            />
          ) : (
            <>
              <CalendarGrid
                // bookings={allBookings}
                bookings={displayBookings}
                selectedStaff={selectedStaff}
                view={calendarView}
                selecteddate={calendarDate}
                // staffList={mergedStaffList}
                staffList={staffList}
                onRefresh={refreshBookings}
              />
              {loading && (
                <GlobalLoader
                  initialPageLoad={true}
                  description="Updating calendar..."
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingsPage;
